import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * 全局多标签页工作台状态
 * tabs: [{ key, label, path, closable }]
 * activeKey: 当前激活的标签 key（通常等于路由 path）
 */
const TabContext = createContext(null);

export function TabProvider({ children }) {
  const [tabs, setTabs] = useState([]);
  const [activeKey, setActiveKey] = useState('');

  // 打开（或激活）一个标签；若已存在同 key 则不重复添加
  const openTab = useCallback((tab) => {
    if (!tab || !tab.key) return;
    setTabs(prev => {
      const exists = prev.find(t => t.key === tab.key);
      if (exists) {
        // 更新 label（比如详情页标题后到）/ 保留原有
        if (tab.label && exists.label !== tab.label) {
          return prev.map(t => t.key === tab.key ? { ...t, label: tab.label } : t);
        }
        return prev;
      }
      return [...prev, { closable: true, ...tab }];
    });
    setActiveKey(tab.key);
  }, []);

  // 更新已存在标签的标题
  const updateTabLabel = useCallback((key, label) => {
    if (!key || !label) return;
    setTabs(prev => prev.map(t => t.key === key ? { ...t, label } : t));
  }, []);

  // 关闭某个标签，返回关闭后的目标 key（由调用方跳转）
  const closeTab = useCallback((key) => {
    let nextKey = null;
    setTabs(prev => {
      const idx = prev.findIndex(t => t.key === key);
      if (idx === -1) return prev;
      const next = prev.filter(t => t.key !== key);
      if (key === activeKey) {
        // 优先激活右侧标签，否则左侧
        const fallback = next[idx] || next[idx - 1] || next[next.length - 1];
        nextKey = fallback ? fallback.key : '';
      }
      return next;
    });
    return nextKey;
  }, [activeKey]);

  const closeOthers = useCallback((key) => {
    setTabs(prev => prev.filter(t => t.key === key || !t.closable));
    setActiveKey(key);
  }, []);

  const closeAll = useCallback(() => {
    setTabs(prev => prev.filter(t => !t.closable));
    setActiveKey('');
  }, []);

  // 重置（登出时用）
  const resetTabs = useCallback(() => {
    setTabs([]);
    setActiveKey('');
  }, []);

  return (
    <TabContext.Provider value={{
      tabs, activeKey, setActiveKey,
      openTab, updateTabLabel, closeTab, closeOthers, closeAll, resetTabs,
    }}>
      {children}
    </TabContext.Provider>
  );
}

export function useTabs() {
  const ctx = useContext(TabContext);
  if (!ctx) throw new Error('useTabs 必须在 TabProvider 内使用');
  return ctx;
}
